import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box, Typography, Paper, CircularProgress, Chip, Divider,
  Table, TableHead, TableBody, TableRow, TableCell, Alert,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import { verifyPrescription } from '../services/api';

// created_at viene naive-UTC del backend — agregar la 'Z' evita que el
// navegador lo interprete como hora local (~3hs adelantado en Argentina).
const asUtc = (d) => new Date(typeof d === 'string' && !d.endsWith('Z') ? d + 'Z' : d);

const fmtDate = (d) =>
  d ? asUtc(d).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—';

const StatusBanner = ({ status, validUntil }) => {
  if (status === 'emitida') {
    return (
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 1.5, p: 2.5,
        bgcolor: '#e8f5e9', border: '2px solid #2e7d32', borderRadius: 2, mb: 3,
      }}>
        <CheckCircleIcon sx={{ color: '#2e7d32', fontSize: 32 }} />
        <Box>
          <Typography variant="h6" fontWeight={700} sx={{ color: '#1b5e20' }}>
            Receta válida
          </Typography>
          <Typography variant="body2" sx={{ color: '#2e7d32' }}>
            Firma digital verificada · Válida hasta {fmtDate(validUntil)}
          </Typography>
        </Box>
      </Box>
    );
  }

  if (status === 'vencida') {
    return (
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 1.5, p: 2.5,
        bgcolor: '#fff3e0', border: '2px solid #e65100', borderRadius: 2, mb: 3,
      }}>
        <WarningAmberIcon sx={{ color: '#e65100', fontSize: 32 }} />
        <Box>
          <Typography variant="h6" fontWeight={700} sx={{ color: '#bf360c' }}>
            Receta vencida
          </Typography>
          <Typography variant="body2" sx={{ color: '#e65100' }}>
            Esta receta superó los 30 días de vigencia desde su emisión.
          </Typography>
        </Box>
      </Box>
    );
  }

  return null;
};

const PrescriptionVerify = () => {
  const [params]        = useSearchParams();
  const token           = params.get('t') || '';
  const [result, setResult] = useState(null);   // { valid, status, prescription }
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');

  useEffect(() => {
    if (!token) {
      setError('No se encontró un token de receta en la URL.');
      setLoading(false);
      return;
    }
    verifyPrescription(token)
      .then(res => setResult(res.data))
      .catch(err => {
        const detail = err.response?.data?.detail || err.message || 'Error al verificar la receta';
        // 410 = vencida (JWT expirado)
        if (err.response?.status === 410) {
          setError('');
          setResult({ valid: false, status: 'vencida_firma', detail });
        } else {
          setError(detail);
        }
      })
      .finally(() => setLoading(false));
  }, [token]);

  const rx = result?.prescription;

  return (
    <Box sx={{
      minHeight: '100vh', bgcolor: '#f5f7fa',
      display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
      pt: { xs: 3, sm: 6 }, px: 2, pb: 6,
    }}>
      <Box sx={{ width: '100%', maxWidth: 700 }}>

        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
          <MedicalServicesIcon sx={{ color: '#1a237e', fontSize: 32 }} />
          <Typography variant="h5" fontWeight={700} sx={{ color: '#1a237e' }}>
            Verificación de receta electrónica
          </Typography>
        </Box>

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        )}

        {/* Error de firma / token inválido */}
        {!loading && (error || result?.status === 'vencida_firma') && (
          <Paper sx={{ p: 3 }}>
            <Box sx={{
              display: 'flex', alignItems: 'center', gap: 1.5, p: 2.5,
              bgcolor: '#fce4ec', border: '2px solid #c62828', borderRadius: 2, mb: 2,
            }}>
              <CancelIcon sx={{ color: '#c62828', fontSize: 32 }} />
              <Box>
                <Typography variant="h6" fontWeight={700} sx={{ color: '#b71c1c' }}>
                  {result?.status === 'vencida_firma' ? 'Receta vencida' : 'Receta inválida'}
                </Typography>
                <Typography variant="body2" sx={{ color: '#c62828' }}>
                  {error || result?.detail}
                </Typography>
              </Box>
            </Box>
            <Typography variant="caption" color="text.disabled">
              Si creés que esto es un error, contactá a la clínica emisora.
            </Typography>
          </Paper>
        )}

        {/* Resultado válido */}
        {!loading && rx && (
          <Paper sx={{ p: 3 }}>
            <StatusBanner status={result.status} validUntil={rx.created_at
              ? new Date(asUtc(rx.created_at).getTime() + 30 * 24 * 3600 * 1000)
              : null}
            />

            {/* Datos del médico */}
            <Typography variant="overline" color="text.secondary" sx={{ fontSize: '0.7rem', letterSpacing: 1 }}>
              Médico prescriptor
            </Typography>
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle1" fontWeight={700}>
                Dr./Dra. {rx.doctor?.name}
              </Typography>
              {rx.doctor?.specialty && (
                <Typography variant="body2" color="text.secondary">{rx.doctor.specialty}</Typography>
              )}
              <Typography variant="body2" color="text.secondary">
                Matrícula: <strong>{rx.doctor?.matricula || '—'}</strong>
              </Typography>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Datos del paciente */}
            <Typography variant="overline" color="text.secondary" sx={{ fontSize: '0.7rem', letterSpacing: 1 }}>
              Paciente
            </Typography>
            <Box sx={{ mb: 2, display: 'flex', gap: 3, flexWrap: 'wrap' }}>
              <Box>
                <Typography variant="body2" color="text.secondary">Nombre</Typography>
                <Typography variant="body1" fontWeight={600}>{rx.patient?.name || '—'}</Typography>
              </Box>
              {rx.patient?.dni && (
                <Box>
                  <Typography variant="body2" color="text.secondary">DNI</Typography>
                  <Typography variant="body1" fontWeight={600}>{rx.patient.dni}</Typography>
                </Box>
              )}
              <Box>
                <Typography variant="body2" color="text.secondary">Fecha de emisión</Typography>
                <Typography variant="body1" fontWeight={600}>{fmtDate(rx.created_at)}</Typography>
              </Box>
            </Box>

            {rx.diagnosis_hint && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="overline" color="text.secondary" sx={{ fontSize: '0.7rem', letterSpacing: 1 }}>
                  Diagnóstico / indicación
                </Typography>
                <Typography variant="body1" sx={{ mt: 0.5 }}>{rx.diagnosis_hint}</Typography>
              </>
            )}

            <Divider sx={{ my: 2 }} />

            {/* Medicamentos */}
            <Typography variant="overline" color="text.secondary" sx={{ fontSize: '0.7rem', letterSpacing: 1 }}>
              Medicamentos ({rx.items?.length})
            </Typography>
            <Table size="small" sx={{ mt: 1 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Medicamento</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Concentración</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Forma</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Cantidad</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Indicaciones</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem' }}>Genérico</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(rx.items || []).map((item, i) => (
                  <TableRow key={i} sx={{ bgcolor: i % 2 === 0 ? 'white' : '#f8f9ff' }}>
                    <TableCell><Typography variant="body2" fontWeight={600}>{item.drug_name}</Typography></TableCell>
                    <TableCell>{item.concentration || '—'}</TableCell>
                    <TableCell>{item.pharmaceutical_form || '—'}</TableCell>
                    <TableCell>{item.quantity || '—'}</TableCell>
                    <TableCell>{item.instructions || '—'}</TableCell>
                    <TableCell>
                      <Chip label={item.is_generic ? 'Sí' : 'No'} size="small"
                        color={item.is_generic ? 'success' : 'default'} variant="outlined" />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {rx.notes && (
              <Alert severity="info" sx={{ mt: 2 }} icon={false}>
                <strong>Notas:</strong> {rx.notes}
              </Alert>
            )}

            <Divider sx={{ my: 2 }} />
            <Typography variant="caption" color="text.disabled">
              Receta N° RX-{String(rx.id).padStart(4, '0')} · Firma digital RS256 verificada ·
              Válida por 30 días desde la emisión
            </Typography>
          </Paper>
        )}

      </Box>
    </Box>
  );
};

export default PrescriptionVerify;
